import { getCartErrorMessage } from '@theme/cart-feedback';

/**
 * <s-card-add-to-cart> — Add to cart on a product card, without its own <form>.
 *
 * Product cards on collection pages live inside the filter <form>, and a form
 * can't contain another form, so this element adds the line itself. Its
 * controls have no `name`, and their input/change events are stopped here so
 * they never trigger the filters.
 *
 * Reads: [data-card-variant] (select or hidden input with the variant id),
 *        [data-card-quantity] (number input), [data-card-add] (button),
 *        [data-add-to-cart-error] (message), data-error-message.
 */
class SCardAddToCart extends HTMLElement {
  #ac;
  #busy = false;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    // Keep card controls from reaching the surrounding filter form.
    for (const type of ['input', 'change']) {
      this.addEventListener(type, (event) => event.stopPropagation(), opts);
    }
    // Switch the button between "Add to cart" and "Pre-order" as the chosen option changes.
    this.addEventListener('change', (event) => {
      if (event.target.matches?.('select[data-card-variant]')) this.#syncLabel();
    }, opts);
    this.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' && event.target instanceof HTMLInputElement) {
        event.preventDefault();
        event.stopPropagation();
        this.#add();
      }
    }, opts);

    this.addEventListener('click', (event) => {
      const button = event.target instanceof Element ? event.target.closest('[data-card-add]') : null;
      if (!button || !this.contains(button)) return;
      event.preventDefault();
      event.stopPropagation();
      this.#add();
    }, opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #line() {
    const control = this.querySelector('[data-card-variant]');
    const variantId = String(control?.value || '').trim();
    if (!variantId) return null;
    const input = this.querySelector('[data-card-quantity]');
    const min = Number.parseInt(input?.min || '1', 10) || 1;
    const parsed = Number.parseInt(input?.value || String(min), 10);
    const quantity = Number.isFinite(parsed) && parsed > 0 ? parsed : min;
    // Pre-order (Timesact) variants carry their selling plan so the order is recorded as a pre-order.
    const source = control?.tagName === 'SELECT' ? control.selectedOptions[0] : control;
    const sellingPlanId = String(source?.dataset.sellingPlan || '').trim();
    return { variantId, quantity, sellingPlanId };
  }

  async #add() {
    if (this.#busy) return;
    const line = this.#line();
    if (!line) return;

    const button = this.querySelector('[data-card-add]');
    this.#setBusy(true, button);
    this.#showError('');

    try {
      if (window.Shopify?.actions?.updateCart) {
        const result = await window.Shopify.actions.updateCart(
          { lines: [{ merchandiseId: line.variantId, quantity: line.quantity, ...(line.sellingPlanId ? { sellingPlanId: line.sellingPlanId } : {}) }] },
          { event: { context: 'product', detail: { productId: this.dataset.productId } } }
        );
        const message = result?.userErrors?.[0]?.message || '';
        if (message) {
          this.#showError(message);
          return;
        }
        await window.Shopify.actions.openCart?.();
      } else {
        // Fallback: classic cart endpoint, then show the cart page.
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(line.variantId), quantity: line.quantity, ...(line.sellingPlanId ? { selling_plan: Number(line.sellingPlanId) } : {}) }] }),
        });
        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          this.#showError(data.description || data.message || this.dataset.errorMessage || '');
          return;
        }
        window.location.href = `${root}cart`;
      }
    } catch (error) {
      this.#showError(getCartErrorMessage(error) || this.dataset.errorMessage || '');
    } finally {
      this.#setBusy(false, button);
    }
  }

  #syncLabel() {
    const button = this.querySelector('[data-card-add]');
    const select = this.querySelector('select[data-card-variant]');
    if (!button || !select) return;
    const isPreorder = Boolean(select.selectedOptions[0]?.dataset.sellingPlan);
    const label = isPreorder ? button.dataset.labelPreorder : button.dataset.labelAdd;
    if (label) button.textContent = label;
  }

  #setBusy(busy, button) {
    this.#busy = busy;
    this.toggleAttribute('aria-busy', busy);
    if (button) {
      button.setAttribute('aria-disabled', busy ? 'true' : 'false');
      button.classList.toggle('opacity-60', busy);
    }
  }

  #showError(message) {
    const error = this.querySelector('[data-add-to-cart-error]');
    if (!error) return;
    error.textContent = message || '';
    error.hidden = !message;
  }
}

if (!customElements.get('s-card-add-to-cart')) {
  customElements.define('s-card-add-to-cart', SCardAddToCart);
}
